import { AiBlockErrorBoundary } from '@Pages/pageRuntime/compiler/AiBlockErrorBoundary';
import { usePageSdkErrorReporter } from '@Pages/pageRuntime/compiler/PageSdkErrorReportingContext';
import { pageSdkErrorKind } from '@Pages/pageRuntime/compiler/reportPageSdkError';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import type React from 'react';

type ErrorBoundaryFallbackProps = {
  error: Error;
  reset: () => void;
};

type ErrorBoundaryProps = {
  /** The section protected from errors. */
  children: React.ReactNode;

  /** Custom fallback content or renderer. The renderer receives the error and a reset function. */
  fallback?: React.ReactNode | ((props: ErrorBoundaryFallbackProps) => React.ReactNode);

  /** Called before the boundary retries its children. */
  onReset?: () => void;

  /** Changing this value clears the current error and retries the section. */
  resetKey?: unknown;
};

/**
 * Isolates a page section so an error there does not replace the entire page. Provide a custom
 * fallback renderer when the section needs context-specific recovery UI; call `reset` to retry it.
 *
 * @example
 * <Sdk.Components.ErrorBoundary
 *   fallback={({ error, reset }) => (
 *     <Sdk.Components.Alert
 *       variant="destructive"
 *       title="Student list unavailable"
 *       description={<Sdk.Components.Button onClick={reset}>Try again</Sdk.Components.Button>}
 *     />
 *   )}
 * >
 *   <StudentList />
 * </Sdk.Components.ErrorBoundary>
 */
export function ErrorBoundary({ children, fallback, onReset, resetKey }: ErrorBoundaryProps) {
  const reportPageSdkError = usePageSdkErrorReporter();

  return (
    <AiBlockErrorBoundary
      source="section"
      resetKey={resetKey}
      onReset={onReset}
      onError={(error, errorInfo) => {
        reportPageSdkError(error, {
          source: 'section',
          componentStack: errorInfo.componentStack
        });
      }}
      renderFallback={({ error, reset }) => {
        const kind = pageSdkErrorKind(error);
        const isCompileError = kind === 'compile';

        if (!isCompileError && typeof fallback === 'function') {
          return (
            <div data-page-sdk-error={kind} className="contents">
              {fallback({ error, reset })}
            </div>
          );
        }

        if (typeof fallback !== 'function' && fallback !== undefined) {
          return (
            <div data-page-sdk-error={kind} className="contents">
              {fallback}
            </div>
          );
        }

        return (
          <div
            role="alert"
            data-page-sdk-error={kind}
            className="flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm"
          >
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                {isCompileError ? "This section couldn't be built." : "This section couldn't load."}
              </p>
              <p className="text-muted-foreground">
                {isCompileError
                  ? 'This section will be available after its code is updated.'
                  : 'Try this section again to continue.'}
              </p>
            </div>
            {!isCompileError ? (
              <Button type="button" variant="outline" size="sm" onClick={reset}>
                Try again
              </Button>
            ) : null}
          </div>
        );
      }}
    >
      {children}
    </AiBlockErrorBoundary>
  );
}
